import { AcceptBid } from "@/app/common/order-dialog-v2/order/accept-bid";
import { CreateBid } from "@/app/common/order-dialog-v2/order/create-bid";
import { CreateListing } from "@/app/common/order-dialog-v2/order/create-listing";
import { BuySelect, SellSelect } from "@/app/common/order-dialog-v2/type-select";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useAsset } from "@/hooks/use-asset";
import { useConnectUser } from "@/hooks/use-connect-user";
import type { OrderDialogState, OrderSide } from "@talentir/shared/common-types";
import { ChevronLeft } from "lucide-react";
import { useEffect, useState } from "react";
import { Buy } from "./order/buy";
import { Success } from "./success";

export type OrderType = "buy" | "create-bid" | "accept-bid" | "create-listing";
type OrderStep = { type: "select-type" } | { type: "order"; orderType: OrderType } | { type: "success" };

export type Asset = {
    id: number;
    blockchainTokenId: string | null;
    title: string;
    contentId: string;
    thumbnail: string;
    talentId: string;
    talentUsername: string | null;
};

export function OrderDialogV2({
    state,
    onStateChange,
}: {
    state: OrderDialogState;
    onStateChange: (state: OrderDialogState) => void;
}) {
    const { asset } = useAsset({ id: state?.assetId });

    const { connect: reconnect, isLoggedIn } = useConnectUser();
    useEffect(() => {
        if (state != null && isLoggedIn !== true) {
            reconnect();
        }
    }, [state, isLoggedIn, reconnect]);

    if (isLoggedIn !== true) {
        return;
    }

    return (
        <Dialog
            open={state != null}
            onOpenChange={(v) => {
                if (!v) {
                    onStateChange(undefined);
                }
            }}
        >
            <DialogContent
                onOpenAutoFocus={(e) => {
                    e.preventDefault();
                }}
            >
                <OrderDialogContent
                    asset={asset}
                    orderSide={state?.side ?? "buy"}
                    onOnramp={() => {
                        onStateChange(undefined);
                    }}
                />
            </DialogContent>
        </Dialog>
    );
}

function OrderDialogContent({
    asset,
    orderSide,
    onOnramp,
}: {
    asset?: Asset;
    orderSide: OrderSide;
    onOnramp: () => void;
}) {
    const [step, setStep] = useState<OrderStep>({ type: "select-type" });

    const goBack = () => {
        if (step.type === "order") {
            setStep({ type: "select-type" });
        }
    };

    return (
        <>
            {step.type === "order" && (
                <div className="absolute top-5 left-4">
                    <button
                        type="button"
                        className="rounded focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        onClick={goBack}
                    >
                        <ChevronLeft />
                    </button>
                </div>
            )}

            {step.type === "select-type" && orderSide === "buy" && (
                <BuySelect
                    asset={asset}
                    onOrderTypeChange={(type) => {
                        setStep({ type: "order", orderType: type });
                    }}
                />
            )}

            {step.type === "select-type" && orderSide === "sell" && (
                <SellSelect
                    asset={asset}
                    onOrderTypeChange={(type) => {
                        setStep({ type: "order", orderType: type });
                    }}
                />
            )}

            {step.type === "order" && step.orderType === "buy" && asset != null && (
                <Buy
                    asset={asset}
                    onComplete={() => {
                        setStep({ type: "success" });
                    }}
                    onOnramp={onOnramp}
                />
            )}

            {step.type === "order" && step.orderType === "create-bid" && asset != null && (
                <CreateBid
                    asset={asset}
                    onComplete={() => {
                        setStep({ type: "success" });
                    }}
                    onOnramp={onOnramp}
                />
            )}

            {step.type === "order" && step.orderType === "accept-bid" && asset != null && (
                <AcceptBid
                    asset={asset}
                    onComplete={() => {
                        setStep({ type: "success" });
                    }}
                />
            )}

            {step.type === "order" && step.orderType === "create-listing" && asset != null && (
                <CreateListing
                    asset={asset}
                    onComplete={() => {
                        setStep({ type: "success" });
                    }}
                />
            )}

            {step.type === "success" && <Success />}
        </>
    );
}
